import { createContext, useContext, useState } from 'react';

const LanguageContext = createContext(null);

// Portal-wide translations
// Keys are used directly in components via t('key')
export const PORTAL_TRANSLATIONS = {
  en: {
    // Offers page
    offersTitle:         'Offers & Promotions Management',
    offersSubtitle:      'Abraaj Water — Products & Offers Control Panel',
    addOffer:            'Add Offer',
    exportExcel:         'Export Excel',
    totalOffers:         'Total Offers',
    activeOffers:        'Active Offers',
    expired:             'Expired',
    uploadTitle:         'Drag & drop Excel file here or click to select',
    uploadSubtitle:      'Supports .xlsx and .xls files — data will be added to existing table',
    chooseFile:          'Choose File',
    deliveryTitle:       'Delivery Schedule & Service Policy',
    edit:                'Edit',
    save:                'Save',
    searchPlaceholder:   'Search offers...',
    filterAll:           'All',
    filterActive:        'Active',
    filterExpired:       'Expired',
    filterInactive:      'Inactive',
    statusActive:        'Active',
    statusExpired:       'Expired',
    statusInactive:      'Inactive',
    emptyTitle:          'No Offers',
    emptySubtitle:       'Upload an Excel file or add an offer manually',
    footerHint:          'Click any cell to edit directly',
    toastImported:       'Imported {count} offers successfully',
    toastEmptyFile:      'File is empty or invalid',
    toastReadError:      'Error reading file',
    toastNoData:         'No data to export',
    toastDeleted:        'Offer deleted',
    toastExported:       'File exported successfully',
    deliveryDefault:
      'If order is placed before 3:00 PM, delivery will be made the next day.\n' +
      'If order is placed after 3:00 PM, delivery will be made in two days.\n' +
      'Free delivery across all Kuwait areas.\n' +
      'Working hours: 7 days a week, 24 hours.',
    // Nav
    offersNav:           'Offers & Promos',
  },
  ar: {
    offersTitle:         'إدارة العروض والترقيات',
    offersSubtitle:      'مياه الأبراج — لوحة تحكم المنتجات والعروض',
    addOffer:            'إضافة عرض',
    exportExcel:         'تصدير Excel',
    totalOffers:         'إجمالي العروض',
    activeOffers:        'العروض النشطة',
    expired:             'منتهية',
    uploadTitle:         'اسحب وأفلت ملف Excel هنا أو انقر للاختيار',
    uploadSubtitle:      'يدعم ملفات .xlsx و .xls — سيتم إضافة البيانات إلى الجدول الحالي',
    chooseFile:          'اختيار ملف',
    deliveryTitle:       'جدول التوصيل وسياسة الخدمة',
    edit:                'تعديل',
    save:                'حفظ',
    searchPlaceholder:   'ابحث عن العروض...',
    filterAll:           'الكل',
    filterActive:        'نشط',
    filterExpired:       'منتهي',
    filterInactive:      'غير نشط',
    statusActive:        'نشط',
    statusExpired:       'منتهي',
    statusInactive:      'غير نشط',
    emptyTitle:          'لا توجد عروض',
    emptySubtitle:       'ارفع ملف Excel أو أضف عرضاً يدوياً',
    footerHint:          'انقر على أي خلية للتعديل مباشرة',
    toastImported:       'تم استيراد {count} عروض بنجاح',
    toastEmptyFile:      'الملف فارغ أو غير صالح',
    toastReadError:      'خطأ في قراءة الملف',
    toastNoData:         'لا توجد بيانات للتصدير',
    toastDeleted:        'تم حذف العرض',
    toastExported:       'تم تصدير الملف بنجاح',
    deliveryDefault:
      'إذا تم الطلب قبل الساعة 3:00 مساءً، سيتم التوصيل في اليوم التالي.\n' +
      'إذا تم الطلب بعد الساعة 3:00 مساءً، سيتم التوصيل في غضون يومين.\n' +
      'توصيل مجاني لجميع مناطق الكويت.\n' +
      'ساعات العمل: 7 أيام في الأسبوع، 24 ساعة.',
    offersNav:           'العروض والترقيات',
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('portalLang') || 'en');

  const toggleLang = () => {
    const next = lang === 'en' ? 'ar' : 'en';
    setLang(next);
    localStorage.setItem('portalLang', next);
  };

  /** Translate a key; supports {count} interpolation */
  const t = (key, vars = {}) => {
    let str = PORTAL_TRANSLATIONS[lang]?.[key] ?? PORTAL_TRANSLATIONS['en']?.[key] ?? key;
    Object.entries(vars).forEach(([k, v]) => {
      str = str.replace(`{${k}}`, v);
    });
    return str;
  };

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, isRTL: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
